import { useEffect, useState, useRef } from 'react';
import CEProductSelector from './CEProductSelector';
import CEProductPreviewer from './CEProductPreviewer';
import { CEProductCustomizationForm } from '../product-customization-form/CEProductCustomizationForm';
import CEAddToCartButton from '../add-to-cart-button/CEAddToCartButton';
import { useCEProductSelector } from '../../bitf-customization-engine/useCEProductSelector';
import { useCEProductCustomization } from '../../bitf-customization-engine/useCEProductCustomization';
import { CEProductCustomizationPreview } from '../../bitf-customization-engine/CEProductCustomizationPreview';
import { useTranslate } from '../../hooks/useTranslate';
import CEProductCollectionSelector from './CEProductCollectionSelector';
import CEProductCustomizationPrice from '../product-customization-price/CEProductCustomizationPrice';
import DisplayAt from '../../components/DisplayAt';

export default function CEQuickBuy({ localization }) {
  const { translate } = useTranslate(localization?.locale);

  // Product customization hook
  const { store, setStore, resetCustomization } = useCEProductCustomization({
    storeName: 'quick-buy',
    watchStoreChanges: true,
    localization,
  });

  // Product selector hook
  const productSelectorHook = useCEProductSelector({ localization });
  const { selectedProduct, selectedVariant } = productSelectorHook;

  const [step, setStep] = useState(1);
  // Ref per scrollare il form drawer
  const drawerFormRef = useRef(null);
  const desktopStep1Ref = useRef(null);
  const desktopStep2Ref = useRef(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const mobileDrawerId = 'quick-buy-customization-drawer';
  const [canContinue, setCanContinue] = useState(false);

  useEffect(() => {
    setCanContinue(
      !!selectedVariant && selectedVariant.availableForSale !== false && store.isLoadingCustomization === false,
    );
  }, [selectedVariant, store.isLoadingCustomization]);

  // Scrolla i contenitori al top quando si cambia step
  useEffect(() => {
    requestAnimationFrame(() => {
      drawerFormRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      desktopStep1Ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
      desktopStep2Ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [step]);

  // Reset function for after successful add to cart
  const handleAddToCartSuccess = () => {
    setStep(1);
    resetCustomization();
    setStore({ currentProductHandle: null });
    setIsMobileDrawerOpen(false);
  };
  useEffect(() => {
    if (selectedProduct?.handle && store.currentProductHandle !== selectedProduct?.handle) {
      setStore({ currentProductHandle: selectedProduct?.handle });
    }
  }, [selectedProduct, setStore]);

  useEffect(() => {
    // Prevent background scrolling when drawer is open
    if (isMobileDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileDrawerOpen]);

  return (
    <>
      <DisplayAt value="desktop">
        <div className="bg-grey-100 md-max:hidden">
          <div className="quick-buy-container container container--lg">
            <div className="quick-buy-previewer-box" style={{ position: 'relative' }}>
              <div style={{ height: step === 1 ? '100%' : '0', overflow: step === 1 ? 'visible' : 'hidden' }}>
                <CEProductPreviewer selectedProduct={selectedProduct} selectedVariant={selectedVariant} />
              </div>
              <div style={{ height: step === 2 ? '100%' : '0', overflow: step === 2 ? 'visible' : 'hidden' }}>
                <CEProductCustomizationPreview
                  customizationHookConfig={{
                    storeName: 'quick-buy',
                    watchStoreChanges: false,
                    language: localization?.locale,
                  }}
                  showProductImage={true}
                  productVariant={selectedVariant}
                />
              </div>
            </div>
            <div className="quick-buy-form-box">
              <div className="quick-buy-form-container">
                <div className="quick-buy-form-header">
                  <div className="stepper-container">
                    <div className="stepper-item">
                      <div className={`stepper-number Body Medium ${step === 1 ? 'active' : ''}`}>1</div>
                      <span className="stepper-text Body Medium Uppercase">
                        {translate('customization.steps.choose_shirt')}
                      </span>
                    </div>
                    <div className="stepper-item">
                      <div className={`stepper-number Body Medium ${step === 2 ? 'active' : ''}`}>2</div>
                      <span className="stepper-text Body Medium Uppercase">
                        {translate('customization.steps.personalize')}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  ref={desktopStep1Ref}
                  style={{ display: step === 1 ? 'block' : 'none' }}
                  className="quick-buy-form-content"
                >
                  {/* Collections */}
                  <div className="md-max:hidden">
                    <CEProductCollectionSelector
                      productSelectorHook={productSelectorHook}
                      localization={localization}
                    />
                  </div>
                  <CEProductSelector productSelectorHook={productSelectorHook} localization={localization} />
                </div>
                <div
                  ref={desktopStep2Ref}
                  style={{ display: step === 2 ? 'block' : 'none' }}
                  className="quick-buy-form-content"
                >
                  <CEProductCustomizationForm
                    customizationHookConfig={{
                      storeName: 'quick-buy',
                      watchStoreChanges: false,
                      language: localization?.locale,
                    }}
                  />
                </div>
                <div className="quick-buy-form-box-footer">
                  <div className="grid justify-center">
                    <CEProductCustomizationPrice
                      storeName={'quick-buy'}
                      language={localization?.locale}
                      locale={localization?.locale}
                      selectVariantPrice={Number(selectedVariant?.price?.amount)}
                      currencyCode={selectedVariant?.price?.currencyCode}
                    />
                    <p className="ExtraSmall d-flex justify-center">{translate('customization.form.refund_info')}</p>
                  </div>
                  {/* Continue Button */}
                  <div className="navigation-buttons-wrapper">
                    {step === 1 && (
                      <button
                        className="ASRomaFont Small ExtraBold  asroma-button button--solid button-primary Uppercase w-full"
                        disabled={!canContinue}
                        onClick={
                          canContinue
                            ? () => {
                                setStep(step + 1);
                              }
                            : undefined
                        }
                      >
                        {translate('customization.steps.continue')}
                      </button>
                    )}
                    {step === 2 && (
                      <button
                        className="ASRomaFont Small ExtraBold  asroma-button button--outline button-primary Uppercase"
                        onClick={() => {
                          if (step === 2) {
                            setStep(step - 1);
                            resetCustomization();
                          }
                        }}
                      >
                        {translate('customization.steps.back')}
                      </button>
                    )}
                    {step === 2 && (
                      <CEAddToCartButton
                        storeName={'quick-buy'}
                        onAddToCartSuccess={handleAddToCartSuccess}
                        language={localization?.locale}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DisplayAt>
      <DisplayAt value="mobile">
        <>
          <div className="bg-grey-100 md:hidden">
            <div className="quick-buy-container">
              <div className="quick-buy-container-header">
                <p className="ASRomaFont Headline-3 Medium Uppercase w-full d-flex justify-center">
                  {translate('customization.form.customize_your_jersey')}
                </p>
              </div>
              {/* Collections */}
              <CEProductCollectionSelector productSelectorHook={productSelectorHook} localization={localization} />
              <div className="quick-buy-previewer-box" style={{ position: 'relative' }}>
                {/* Product Previewer */}
                <CEProductPreviewer selectedProduct={selectedProduct} selectedVariant={selectedVariant} />
              </div>

              <div className="w-full justify-center d-flex">
                <button
                  className="ASRomaFont Small ExtraBold asroma-button button--solid button-primary Uppercase w-full"
                  aria-controls={mobileDrawerId}
                  onClick={() => setIsMobileDrawerOpen(!isMobileDrawerOpen)}
                >
                  {translate('customization.form.customize')}
                </button>
              </div>
            </div>
          </div>

          <div
            id={mobileDrawerId}
            className={`product-customization-drawer product-customization-drawer-quick-buy ${isMobileDrawerOpen ? 'product-customization-drawer--open' : 'product-customization-drawer--closed'}`}
          >
            <div className="product-customization-drawer__container">
              {/* Close button */}
              <div className="product-customization-drawer__header">
                <button
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="close-button tap-area circle-button"
                  aria-label="Close drawer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="20.06" viewBox="0 0 20.06 20.06">
                    <g id="Raggruppa_14429" data-name="Raggruppa 14429" transform="translate(10.16 472.19) rotate(45)">
                      <g id="Raggruppa_3692" data-name="Raggruppa 3692" transform="translate(-338.704 -326.704)">
                        <line
                          id="Linea_8"
                          data-name="Linea 8"
                          x2="24"
                          fill="none"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-width="2"
                        />
                      </g>
                      <g
                        id="Raggruppa_3738"
                        data-name="Raggruppa 3738"
                        transform="translate(-327.073 -314.704) rotate(-90)"
                      >
                        <line
                          id="Linea_8-2"
                          data-name="Linea 8"
                          x2="24"
                          fill="none"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-width="2"
                        />
                      </g>
                    </g>
                  </svg>
                </button>
              </div>
              <div className="product-customization-drawer__product-preview">
                <div className="product-customization-drawer__product-preview-content">
                  <CEProductCustomizationPreview
                    customizationHookConfig={{
                      storeName: 'quick-buy',
                      watchStoreChanges: false,
                      language: localization?.locale,
                    }}
                    showProductImage={true}
                    mixBlendMode={true}
                    productVariant={selectedVariant}
                  />
                </div>
                <div className="quick-buy-form-header">
                  <div className="stepper-container">
                    <div className="stepper-item">
                      <div className={`stepper-number-drawer Body Medium ${step === 1 ? 'active' : ''}`}>1</div>
                      <span className="stepper-text Small Medium Uppercase">
                        {translate('customization.steps.choose_shirt')}
                      </span>
                    </div>
                    <div className="stepper-item">
                      <div className={`stepper-number-drawer Body Medium ${step === 2 ? 'active' : ''}`}>2</div>
                      <span className="stepper-text Small Medium Uppercase">
                        {translate('customization.steps.personalize')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="product-customization-drawer__form"
                ref={drawerFormRef}
                style={{ overflowY: 'auto', maxHeight: '60vh' }}
              >
                <div style={{ display: step === 1 ? 'block' : 'none', width: '100%' }}>
                  <CEProductSelector productSelectorHook={productSelectorHook} localization={localization} />
                </div>
                <div style={{ display: step === 2 ? 'block' : 'none', width: '100%' }}>
                  <CEProductCustomizationForm
                    customizationHookConfig={{
                      storeName: 'quick-buy',
                      watchStoreChanges: false,
                      language: localization?.locale,
                    }}
                  />
                </div>
              </div>

              <div className="product-customization-drawer__footer d-flex justify-center">
                <CEProductCustomizationPrice
                  storeName={'quick-buy'}
                  language={localization?.locale}
                  locale={localization?.locale}
                />
                {/* Continue Button */}
                {step === 1 && (
                  <button
                    className="ASRomaFont Small ExtraBold  asroma-button button--solid button-primary Uppercase w-full"
                    disabled={!canContinue}
                    onClick={
                      canContinue
                        ? () => {
                            setStep(step + 1);
                          }
                        : undefined
                    }
                  >
                    {translate('customization.steps.continue')}
                  </button>
                )}
                {step === 2 && (
                  <div className="d-flex gap-2">
                    <button
                      className="ASRomaFont Small ExtraBold  asroma-button button--outline button-primary Uppercase  w-full"
                      onClick={() => {
                        if (step === 2) {
                          setStep(step - 1);
                          resetCustomization();
                        }
                      }}
                    >
                      {translate('customization.steps.back')}
                    </button>
                    <CEAddToCartButton
                      storeName={'quick-buy'}
                      onAddToCartSuccess={handleAddToCartSuccess}
                      language={localization?.locale}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      </DisplayAt>
    </>
  );
}
